// @systeme: interface
// L'accès support (DEC-011) : ouvrir un accès à un foyer sur demande écrite, pour une
// durée courte, lire ce qu'il permet (jamais le privé ni la santé), le fermer. Chaque
// ouverture et chaque lecture vont dans le journal ; le foyer voit que c'est ouvert.
import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { AccesSupport, FoyerAdmin, VueSupport } from '@foyer/shared';
import { Chargement } from '../../composants/Chargement';
import { useMessages } from '../../composants/Messages';
import { api } from '../../lib/api';
import { formaterHeure, formaterJourCourt, jourDe } from '../../lib/dates';

export function Support() {
  const { dire, direErreur } = useMessages();
  const [acces, setAcces] = useState<AccesSupport[] | undefined>();
  const [foyers, setFoyers] = useState<FoyerAdmin[]>([]);
  const [espaceId, setEspaceId] = useState('');
  const [motif, setMotif] = useState('');
  const [duree, setDuree] = useState('24');
  const [vue, setVue] = useState<VueSupport | undefined>();

  const charger = useCallback(() => {
    api.admin.accesSupport().then(setAcces).catch(direErreur);
    api.admin.foyers().then(setFoyers).catch(direErreur);
  }, [direErreur]);
  useEffect(charger, [charger]);

  async function ouvrir() {
    try {
      await api.admin.ouvrirSupport({ espaceId, motif: motif.trim(), dureeHeures: Number(duree) });
      setMotif('');
      dire('Accès ouvert. Le foyer le voit dans son app.');
      charger();
    } catch (e) {
      direErreur(e);
    }
  }

  async function lire(id: string) {
    try {
      setVue(await api.admin.vueSupport(id));
    } catch (e) {
      direErreur(e);
    }
  }

  async function fermer(id: string) {
    try {
      await api.admin.revoquerSupport(id);
      if (vue?.acces.id === id) setVue(undefined);
      dire('Accès fermé.');
      charger();
    } catch (e) {
      direErreur(e);
    }
  }

  if (!acces) return <Chargement />;
  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">
        Seulement à la demande écrite de l’utilisatrice, pour une durée courte. Elle voit dans son
        app qu’un accès est ouvert ; chaque lecture est inscrite au journal, pour toujours. Le privé
        et la santé restent hors de portée.
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <TextField
            select
            size="small"
            label="Foyer"
            value={espaceId}
            onChange={(e) => setEspaceId(e.target.value)}
          >
            {foyers.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.nom} · {f.membres} membre{f.membres > 1 ? 's' : ''} · créé le{' '}
                {formaterJourCourt(jourDe(f.creeLe))} · {f.id.slice(0, 8)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="La demande écrite (qui, quand, quoi)"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
          <TextField
            select
            size="small"
            label="Durée"
            value={duree}
            onChange={(e) => setDuree(e.target.value)}
            sx={{ maxWidth: 200 }}
          >
            <MenuItem value="2">Deux heures</MenuItem>
            <MenuItem value="24">Un jour</MenuItem>
            <MenuItem value="168">Une semaine</MenuItem>
          </TextField>
          <Button
            variant="contained"
            disabled={!espaceId || motif.trim().length < 10}
            onClick={() => void ouvrir()}
            sx={{ alignSelf: 'flex-start' }}
          >
            Ouvrir l’accès
          </Button>
        </Stack>
      </Paper>
      {acces.map((a) => (
        <Paper key={a.id} variant="outlined" sx={{ p: 1.5 }}>
          <Typography sx={{ fontWeight: 600 }}>
            {a.nomEspace}{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              ·{' '}
              {a.ouvert
                ? `ouvert jusqu’au ${formaterJourCourt(jourDe(a.expireLe))} ${formaterHeure(a.expireLe)}`
                : 'fermé'}
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {a.motif}
          </Typography>
          {a.ouvert && (
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Button size="small" onClick={() => void lire(a.id)}>
                Lire
              </Button>
              <Button size="small" color="inherit" onClick={() => void fermer(a.id)}>
                Fermer l’accès
              </Button>
            </Stack>
          )}
        </Paper>
      ))}
      {vue && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h2" component="h2" gutterBottom>
            {vue.espace.nom}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Personnes : {vue.personnes.map((p) => p.prenom).join(', ') || 'aucune'}
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>Éléments (ni privés, ni santé)</Typography>
          {vue.elements.map((e) => (
            <Typography key={e.id} variant="body2">
              {e.titre}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                · {e.type} · {e.statut} · {e.origine}
                {e.debutLe ? ` · ${formaterJourCourt(jourDe(e.debutLe))}` : ''}
              </Typography>
            </Typography>
          ))}
          <Typography sx={{ fontWeight: 600, mt: 1 }}>Captures</Typography>
          {vue.captures.map((c) => (
            <Typography key={c.id} variant="body2">
              {formaterJourCourt(jourDe(c.creeLe))} · {c.statut}
              {c.erreur ? ` · ${c.erreur}` : ''}
            </Typography>
          ))}
        </Paper>
      )}
    </Stack>
  );
}
